import { useEffect, useRef, useState } from "react";

import usePopover from "./usePopover";
import "../field-popovers.css";

/**
 * A dropdown in the app's own style, in place of the browser's <select>
 * list. Sits inside an .rf-input-shell.
 *
 * `options` are strings or { value, label }. Keyboard: Enter / Space /
 * ArrowDown opens it, arrows move, Enter picks, Escape closes, and typing a
 * letter jumps to the first option starting with it.
 */
const norm = (o) => (typeof o === "string" ? { value: o, label: o } : o);

export default function SelectMenu({ value, options, placeholder, onChange, disabled }) {
  const items = (options || []).map(norm);
  const pop = usePopover(Math.min(items.length, 8) * 36 + 12);
  const [active, setActive] = useState(-1);
  const listRef = useRef(null);
  const current = items.find((o) => o.value === value);

  useEffect(() => {
    if (!pop.open || !listRef.current || active < 0) return;
    const el = listRef.current.children[active];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [active, pop.open]);

  function open() {
    setActive(Math.max(0, items.findIndex((o) => o.value === value)));
    pop.show();
  }
  function choose(o) {
    onChange(o.value);
    pop.hide();
  }

  function onKeyDown(e) {
    if (!pop.open) {
      if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        open();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (items[active]) choose(items[active]);
    } else if (e.key === "Tab") {
      pop.hide();
    } else if (e.key.length === 1) {
      const i = items.findIndex((o) =>
        String(o.label).toLowerCase().startsWith(e.key.toLowerCase())
      );
      if (i >= 0) setActive(i);
    }
  }

  return (
    <div className="rf-fp" ref={pop.ref}>
      <button
        type="button"
        className="rf-fp-btn"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={pop.open}
        onClick={() => (pop.open ? pop.hide() : open())}
        onKeyDown={onKeyDown}
      >
        <span className={`rf-fp-value${current ? "" : " is-empty"}`}>
          {current ? current.label : placeholder || "Choose"}
        </span>
        <span className={`rf-fp-caret${pop.open ? " is-open" : ""}`} aria-hidden="true">
          ▾
        </span>
      </button>

      {pop.open && (
        <div
          className={`rf-fp-pop rf-fp-menu${pop.openUp ? " is-up" : ""}`}
          role="listbox"
          ref={listRef}
        >
          {items.map((o, i) => (
            <div
              key={o.value}
              role="option"
              aria-selected={o.value === value}
              className={`rf-fp-opt${i === active ? " is-active" : ""}${
                o.value === value ? " is-chosen" : ""
              }`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(o)}
            >
              <span>{o.label}</span>
              {o.value === value && <span className="rf-fp-tick">✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
